/**
 * Image Rotator widget.
 *
 * Time-driven state machine. Rather than chaining setTimeouts (fragile —
 * any useEffect cleanup or closure surprise breaks the chain), we run a
 * single 100ms tick loop and derive both `activeIndex` and `phase` from
 * the elapsed time since `cycleStartedAt`.
 *
 * The cycle for image N is:
 *     [0, transitionMs)              → entering
 *     [transitionMs, +holdMs)        → holding
 *     [holdMs+trans, +transitionMs)  → exiting
 *     [holdMs+2*trans, +gapMs)       → gap
 *     >= total                       → pick next image, restart cycle
 *
 * Pause freezes the elapsed clock; resume continues from where it was.
 * Skip jumps directly into the exit phase so the current image leaves
 * cleanly before the next one enters.
 */
import { useEffect, useRef, useState } from 'react';

type Order = 'sequential' | 'shuffle';
type ObjectFit = 'contain' | 'cover';
type Background = 'transparent' | 'blur' | 'solid';
type Position =
  | 'top-left'
  | 'top-center'
  | 'top-right'
  | 'center-left'
  | 'center'
  | 'center-right'
  | 'bottom-left'
  | 'bottom-center'
  | 'bottom-right';
type Entrance =
  | 'none'
  | 'fade-in'
  | 'slide-left'
  | 'slide-right'
  | 'slide-down'
  | 'pop'
  | 'zoom-in'
  | 'rotate-in';
type Exit = 'none' | 'fade-out' | 'slide-left' | 'slide-right' | 'shrink' | 'spin-out';
type Idle =
  | 'none'
  | 'float'
  | 'pulse'
  | 'shake'
  | 'wobble'
  | 'spin'
  | 'spin-slow'
  | 'bounce'
  | 'glitch'
  | 'neon-glow';
type Intensity = 'low' | 'medium' | 'high';

interface Settings {
  enabled: boolean;
  order: Order;
  transitionMs: number;
  holdMs: number;
  gapMs: number;
  width: number;
  height: number;
  position: Position;
  objectFit: ObjectFit;
  showCaptions: boolean;
  background: Background;
  backgroundColor: string;
  borderRadius: number;
  borderGlowColor: string | null;
  paused: boolean;
  entranceAnimation: Entrance;
  exitAnimation: Exit;
  idleAnimation: Idle;
  animationIntensity: Intensity;
  glowColor: string;
}

interface ImageEntry {
  id: string;
  url: string;
  caption: string | null;
  holdOverrideMs: number | null;
}

interface PublicPayload {
  streamerId: string;
  isEnabled: boolean;
  settings: Settings;
  images: ImageEntry[];
}

type Phase = 'entering' | 'holding' | 'exiting' | 'gap';

export interface ImageRotatorControlEvent {
  type: 'control' | 'settings-changed' | 'images-changed';
  action?: 'play' | 'pause' | 'skip';
  ts?: number;
}

function anchorStyle(p: Position): React.CSSProperties {
  const base: React.CSSProperties = { position: 'absolute' };
  switch (p) {
    case 'top-left':
      return { ...base, top: 24, left: 24 };
    case 'top-center':
      return { ...base, top: 24, left: '50%', transform: 'translateX(-50%)' };
    case 'top-right':
      return { ...base, top: 24, right: 24 };
    case 'center-left':
      return { ...base, top: '50%', left: 24, transform: 'translateY(-50%)' };
    case 'center':
      return { ...base, top: '50%', left: '50%', transform: 'translate(-50%, -50%)' };
    case 'center-right':
      return { ...base, top: '50%', right: 24, transform: 'translateY(-50%)' };
    case 'bottom-left':
      return { ...base, bottom: 24, left: 24 };
    case 'bottom-center':
      return { ...base, bottom: 24, left: '50%', transform: 'translateX(-50%)' };
    case 'bottom-right':
    default:
      return { ...base, bottom: 24, right: 24 };
  }
}

function intensityMul(i: Intensity): number {
  if (i === 'low') return 0.5;
  if (i === 'high') return 2;
  return 1;
}

function shuffle<T>(arr: T[]): T[] {
  const out = arr.slice();
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    const tmp = out[i]!;
    out[i] = out[j]!;
    out[j] = tmp;
  }
  return out;
}

export function ImageRotatorWidget({
  token,
  controlEvent,
}: {
  token: string;
  controlEvent: ImageRotatorControlEvent | null;
}) {
  const [payload, setPayload] = useState<PublicPayload | null>(null);
  // Rendering state — derived from the scheduler ref on each tick.
  const [renderIndex, setRenderIndex] = useState<number | null>(null);
  const [renderPhase, setRenderPhase] = useState<Phase>('gap');

  // Scheduler refs — single source of truth, never wiped by React effects.
  const payloadRef = useRef<PublicPayload | null>(null);
  /** Index of the image currently in its cycle (entering/holding/exiting/gap). */
  const currentIndexRef = useRef<number | null>(null);
  /** Wall-clock when the current cycle began (entering phase t=0). */
  const cycleStartedAtRef = useRef<number>(0);
  /** Frozen elapsed when paused. -1 = not paused. */
  const pausedElapsedRef = useRef<number>(-1);
  /** Pre-computed shuffle queue (indices) — refilled when drained. */
  const queueRef = useRef<number[]>([]);

  async function fetchState() {
    try {
      const res = await fetch(`/api/image-rotator/public?token=${encodeURIComponent(token)}`, {
        cache: 'no-store',
      });
      if (!res.ok) return;
      const json = (await res.json()) as PublicPayload;
      payloadRef.current = json;
      // Paused flag is server-persisted — re-sync on each fetch.
      if (json.settings.paused) {
        if (pausedElapsedRef.current < 0) pausedElapsedRef.current = 0;
      } else {
        pausedElapsedRef.current = -1;
      }
      // Clear shuffle queue so the new image list rebuilds it.
      queueRef.current = [];
      // Clamp current index if list shrank.
      if (currentIndexRef.current != null && currentIndexRef.current >= json.images.length) {
        currentIndexRef.current = json.images.length === 0 ? null : 0;
        cycleStartedAtRef.current = Date.now();
      }
      setPayload(json);
    } catch {
      /* ignore */
    }
  }

  function pickNext(): number | null {
    const p = payloadRef.current;
    if (!p || p.images.length === 0) return null;
    const cur = currentIndexRef.current;
    if (p.settings.order === 'sequential') {
      if (cur == null) return 0;
      return (cur + 1) % p.images.length;
    }
    if (queueRef.current.length === 0) {
      const fresh = shuffle(p.images.map((_, i) => i));
      // Avoid repeating the current image as the next one.
      if (fresh.length > 1 && cur != null && fresh[0] === cur) {
        const tmp = fresh[0]!;
        fresh[0] = fresh[1]!;
        fresh[1] = tmp;
      }
      queueRef.current = fresh;
    }
    return queueRef.current.shift() ?? null;
  }

  function holdMsFor(idx: number | null, s: Settings): number {
    if (idx == null) return s.holdMs;
    const img = payloadRef.current?.images[idx];
    return img?.holdOverrideMs ?? s.holdMs;
  }

  // Single tick loop. 100ms is fine — phase transitions are visual and
  // CSS-driven, the tick just decides which class to apply.
  useEffect(() => {
    if (!payload || payload.images.length === 0) {
      setRenderIndex(null);
      setRenderPhase('gap');
      return;
    }

    const tick = () => {
      const p = payloadRef.current;
      if (!p || p.images.length === 0) return;

      // Paused: don't advance the clock; just hold the last rendered frame.
      if (pausedElapsedRef.current >= 0) return;

      const s = p.settings;
      const now = Date.now();

      // Bootstrap: first image enters immediately.
      if (currentIndexRef.current == null) {
        const first = pickNext();
        if (first == null) return;
        currentIndexRef.current = first;
        cycleStartedAtRef.current = now;
      }

      let elapsed = now - cycleStartedAtRef.current;
      const trans = Math.max(0, s.transitionMs);
      const hold = Math.max(0, holdMsFor(currentIndexRef.current, s));
      const gap = Math.max(0, s.gapMs);
      const total = trans + hold + trans + gap;

      // Cycle ended → pick next image and restart timeline.
      if (elapsed >= total) {
        const nxt = pickNext();
        if (nxt == null) return;
        currentIndexRef.current = nxt;
        cycleStartedAtRef.current = now;
        elapsed = 0;
      }

      // Phase from elapsed.
      let nextPhase: Phase;
      if (elapsed < trans) nextPhase = 'entering';
      else if (elapsed < trans + hold) nextPhase = 'holding';
      else if (elapsed < trans + hold + trans) nextPhase = 'exiting';
      else nextPhase = 'gap';

      // Apply to React state if changed.
      if (renderIndexRef.current !== currentIndexRef.current) {
        renderIndexRef.current = currentIndexRef.current;
        setRenderIndex(currentIndexRef.current);
      }
      if (renderPhaseRef.current !== nextPhase) {
        renderPhaseRef.current = nextPhase;
        setRenderPhase(nextPhase);
      }
    };

    // Run once immediately so the first image appears without a 100ms wait.
    tick();
    const interval = setInterval(tick, 100);
    return () => clearInterval(interval);
    // Restart the interval only when the image list size changes — the
    // tick body reads everything else from refs, so settings updates
    // don't need an interval restart.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payload?.images.length]);

  // Mirror render state into refs so the tick can check for changes
  // without depending on closures.
  const renderIndexRef = useRef<number | null>(null);
  const renderPhaseRef = useRef<Phase>('gap');
  useEffect(() => {
    renderIndexRef.current = renderIndex;
  }, [renderIndex]);
  useEffect(() => {
    renderPhaseRef.current = renderPhase;
  }, [renderPhase]);

  // Initial fetch.
  useEffect(() => {
    void fetchState();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Dashboard control events.
  useEffect(() => {
    if (!controlEvent) return;
    if (controlEvent.type === 'settings-changed' || controlEvent.type === 'images-changed') {
      void fetchState();
      return;
    }
    if (controlEvent.type === 'control') {
      const p = payloadRef.current;
      if (!p) return;
      if (controlEvent.action === 'pause') {
        if (pausedElapsedRef.current < 0) {
          pausedElapsedRef.current = Date.now() - cycleStartedAtRef.current;
        }
        return;
      }
      if (controlEvent.action === 'play') {
        if (pausedElapsedRef.current >= 0) {
          cycleStartedAtRef.current = Date.now() - pausedElapsedRef.current;
          pausedElapsedRef.current = -1;
        }
        return;
      }
      if (controlEvent.action === 'skip') {
        // Jump straight to the gap → next-cycle boundary so the next tick
        // picks a fresh image.
        const s = p.settings;
        const total = s.transitionMs * 2 + holdMsFor(currentIndexRef.current, s) + s.gapMs;
        cycleStartedAtRef.current = Date.now() - total;
        if (pausedElapsedRef.current >= 0) pausedElapsedRef.current = total;
        return;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [controlEvent]);

  // ─── Render ───────────────────────────────────────────────────────
  if (!payload || !payload.isEnabled || !payload.settings.enabled) return null;
  if (payload.images.length === 0) return null;
  if (renderIndex == null) return null;
  if (renderPhase === 'gap') return null;
  const img = payload.images[renderIndex];
  if (!img) return null;

  const s = payload.settings;

  const animClasses: string[] = ['hv-anim-root'];
  if (renderPhase === 'entering' && s.entranceAnimation !== 'none') {
    animClasses.push(`hv-entrance-${s.entranceAnimation}`);
  } else if (renderPhase === 'exiting' && s.exitAnimation !== 'none') {
    animClasses.push(`hv-exit-${s.exitAnimation}`);
  } else if (renderPhase === 'holding' && s.idleAnimation !== 'none') {
    animClasses.push(`hv-idle-${s.idleAnimation}`);
  }

  const cssVars: React.CSSProperties & Record<string, string | number> = {
    ['--hv-anim-speed']: 1,
    ['--hv-anim-intensity']: intensityMul(s.animationIntensity),
  };
  if (s.idleAnimation === 'neon-glow') cssVars['--hv-glow-color'] = s.glowColor;
  if (renderPhase === 'entering' || renderPhase === 'exiting') {
    cssVars.animationDuration = `${s.transitionMs}ms`;
  }

  const backdropStyle: React.CSSProperties = {
    width: s.width,
    height: s.height,
    borderRadius: s.borderRadius,
    overflow: 'hidden',
    position: 'relative',
    ...(s.background === 'solid'
      ? { background: s.backgroundColor }
      : s.background === 'blur'
        ? {
            background: 'rgba(8,8,16,0.45)',
            backdropFilter: 'blur(12px)',
            WebkitBackdropFilter: 'blur(12px)',
          }
        : { background: 'transparent' }),
    ...(s.borderGlowColor
      ? {
          boxShadow: `0 0 24px ${s.borderGlowColor}, 0 0 6px ${s.borderGlowColor} inset`,
          border: `1px solid ${s.borderGlowColor}`,
        }
      : {}),
  };

  return (
    <div style={anchorStyle(s.position)}>
      <div style={backdropStyle}>
        <div
          key={`${img.id}-${renderPhase}`}
          className={animClasses.join(' ')}
          style={{
            ...cssVars,
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
          }}
        >
          <img
            src={img.url}
            alt=""
            style={{
              width: '100%',
              height: '100%',
              objectFit: s.objectFit,
              display: 'block',
            }}
            draggable={false}
          />
        </div>
        {s.showCaptions && img.caption && renderPhase === 'holding' && (
          <div
            style={{
              position: 'absolute',
              left: 0,
              right: 0,
              bottom: 0,
              padding: '12px 16px',
              background:
                'linear-gradient(0deg, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.45) 60%, rgba(0,0,0,0) 100%)',
              color: 'white',
              fontFamily: "'Poppins', system-ui, sans-serif",
              fontSize: 14,
              fontWeight: 600,
              letterSpacing: 0.4,
              textShadow: '0 1px 2px rgba(0,0,0,0.7)',
              pointerEvents: 'none',
            }}
          >
            {img.caption}
          </div>
        )}
      </div>
    </div>
  );
}
